import React, { useState } from 'react';
import { UserRole } from '../../types';
import { api } from '../../services/api';
import { GoogleSignInButton, GoogleSignInButtonCustom } from '../GoogleSignInButton';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { TurnstileWidget, turnstileEnabled } from './TurnstileWidget';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLogin: (user: any) => void;
    isAdminRoute?: boolean;
    isMainRoute?: boolean;
}

type AuthMode = 'login' | 'register';
type RegisterRole = 'student' | 'teacher' | 'consultant' | 'admin';

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLogin, isAdminRoute = false, isMainRoute = false }) => {
    const [mode, setMode] = useState<AuthMode>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [selectedRole, setSelectedRole] = useState<RegisterRole>('student');
    const [branch, setBranch] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string>('');

    if (!isOpen) return null;

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (turnstileEnabled && !turnstileToken) {
            setError('Lütfen robot olmadığınızı doğrulayın.');
            return;
        }
        setLoading(true);

        try {
            const user = await api.login(email, password, undefined, undefined, undefined, turnstileToken || undefined);
            if (!user) {
                setError('E-posta veya şifre hatalı!');
                setLoading(false);
                return;
            }
            if (isAdminRoute && user?.role !== 'admin') {
                setError('Bu sayfaya erişim yetkiniz yok!');
                setLoading(false);
                return;
            }

            // Restrict student access if they don't have an active package
            if (user?.role === 'student' && user?.packageStatus !== 'active') {
                setError('Sisteme giriş yapabilmek için aktif bir paketinizin olması gerekmektedir.');
                setLoading(false);
                return;
            }

            onLogin(user);
            // For admin route, redirect to admin panel
            if (isAdminRoute) {
                window.history.replaceState(null, '', '/admin');
            } else {
                onClose();
            }
        } catch (err: any) {
            setError(err.message || 'Giriş yapılırken bir hata oluştu veya e-posta/şifre hatalı!');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            let newUser: any = {
                id: Date.now().toString(),
                name,
                email,
                password,
                phoneNumber: phone,
            };

            if (selectedRole === 'teacher') {
                newUser = {
                    ...newUser,
                    role: 'teacher',
                    branch: branch || 'Matematik',
                    appointments: [],
                    blockedSlots: [],
                    approvalStatus: 'pending' // As per requirement: other roles continue to need admin approval
                };
                await api.createTeacher(newUser);
            } else if (selectedRole === 'consultant') {
                newUser = {
                    ...newUser,
                    role: 'education_consultant',
                    studentIds: [],
                    appointments: [],
                    blockedSlots: [],
                    approvalStatus: 'pending' // As per requirement: other roles continue to need admin approval
                };
                await api.createConsultant(newUser);
            } else if (selectedRole === 'admin') {
                // Admin kayıtları sadece mevcut adminler tarafından yapılabilir
                setError('Yetkili hesapları sadece sistem yöneticileri tarafından oluşturulabilir.');
                setLoading(false);
                return;
            } else {
                // If selectedRole is student or any other unexpected role on this path
                setError('Bu sayfadan öğrenci kaydı yapılamaz. Lütfen ana sayfadaki paketleri inceleyin.');
                setLoading(false);
                return;
            }

            setSuccess('Kayıt başarılı! Giriş yapabilirsiniz.');
            setTimeout(() => {
                setMode('login');
                setSuccess('');
            }, 2000);
        } catch (err) {
            setError('Kayıt sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Note: Student role registration is removed from this modal.
    // They must register via the Landing Page checkout flow.
    const roles = [
        { id: 'teacher', label: 'Öğretmen', icon: '👨‍🏫', color: 'from-orange-500 to-amber-500' },
        { id: 'consultant', label: 'Danışman', icon: '🫂', color: 'from-purple-500 to-pink-500' }
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className="relative z-10 w-full max-w-md mx-4 animate-in fade-in zoom-in duration-300">
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600/30 to-cyan-600/30 p-6 text-center relative">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
                        >
                            ✕
                        </button>
                        <img
                            src="/supervisor-logo-2026.png"
                            alt="Supervisor"
                            className="w-20 h-20 mx-auto mb-3 object-contain"
                        />
                        <h2 className="text-2xl font-bold text-white">
                            {mode === 'login' ? (isAdminRoute ? 'Admin Girişi' : 'Hoş Geldiniz') : 'Hesap Oluştur'}
                        </h2>
                        <p className="text-blue-100/70 text-sm mt-1">
                            {mode === 'login' ? (isAdminRoute ? 'Yönetici paneline erişim' : 'Hesabınıza giriş yapın') : 'Yeni bir hesap oluşturun'}
                        </p>
                    </div>

                    {/* Content */}
                    <div className="p-6 max-h-[60vh] overflow-y-auto">
                        {/* Removed Registration Tab for Students on Main Route and for Admins. Register is only available on /educator */}
                        {(!isMainRoute && !isAdminRoute) && (
                            <div className="flex bg-white/5 p-1 rounded-xl mb-6">
                                <button
                                    onClick={() => setMode('login')}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${mode === 'login' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                                >
                                    Giriş Yap
                                </button>
                                <button
                                    onClick={() => setMode('register')}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${mode === 'register' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-800'}`}
                                >
                                    Kayıt Ol
                                </button>
                            </div>
                        )}

                        {mode === 'login' ? (
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">E-posta</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                                        placeholder="ornek@supervisor.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Şifre</label>
                                    <input
                                        type="password"
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                                        placeholder="••••••••"
                                    />
                                    {!isAdminRoute && (
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotPassword(true)}
                                            className="text-sm text-blue-300 hover:text-blue-200 mt-2 block w-full text-right transition-colors"
                                        >
                                            Şifremi Unuttum
                                        </button>
                                    )}
                                </div>

                                {error && (
                                    <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-2 rounded-lg text-sm">
                                        {error}
                                    </div>
                                )}

                                <TurnstileWidget
                                    onVerify={(token) => setTurnstileToken(token)}
                                    onExpire={() => setTurnstileToken('')}
                                />

                                <button
                                    type="submit"
                                    disabled={loading || (turnstileEnabled && !turnstileToken)}
                                    className={`w-full py-3 ${isAdminRoute ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600' : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'} text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50`}
                                >
                                    {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
                                </button>


                                {/* Links to other login pages */}
                                {!isAdminRoute && (
                                    <div className="mt-4 text-center">
                                        <a href="/educator" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                                            👨‍🏫 Öğretmen veya Danışman mısınız? Eğitmen Girişi
                                        </a>
                                    </div>
                                )}
                            </form>
                        ) : (
                            <form onSubmit={handleRegister} className="space-y-4">
                                {/* Student registration only - other roles use /educator */}

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Ad Soyad</label>
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                        placeholder="Adınız Soyadınız"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">E-posta</label>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                        placeholder="ornek@supervisor.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Şifre</label>
                                    <input
                                        type="password"
                                        required
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Telefon</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                        placeholder="05XX XXX XX XX"
                                    />
                                </div>

                                {/* Role selection for educator route */}
                                {!isMainRoute && (
                                    <div>
                                        <label className="block text-sm font-medium text-white/80 mb-1">Rol</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {roles.map((role) => (
                                                <button
                                                    key={role.id}
                                                    type="button"
                                                    onClick={() => setSelectedRole(role.id as RegisterRole)}
                                                    className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${selectedRole === role.id
                                                        ? `border-blue-500 bg-gradient-to-br ${role.color} text-white shadow-lg`
                                                        : 'border-white/20 bg-white/5 text-white/70 hover:border-white/40 hover:text-white'
                                                        }`}
                                                >
                                                    <span className="text-3xl mb-2">{role.icon}</span>
                                                    <span className="font-semibold text-sm">{role.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedRole === 'teacher' && (
                                    <div>
                                        <label className="block text-sm font-medium text-white/80 mb-1">Branş</label>
                                        <select
                                            value={branch}
                                            onChange={(e) => setBranch(e.target.value)}
                                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                                            title="Branş Seçin"
                                        >
                                            <option value="Matematik" className="bg-slate-800">Matematik</option>
                                            <option value="Fizik" className="bg-slate-800">Fizik</option>
                                            <option value="Kimya" className="bg-slate-800">Kimya</option>
                                            <option value="Biyoloji" className="bg-slate-800">Biyoloji</option>
                                            <option value="Türkçe" className="bg-slate-800">Türkçe</option>
                                            <option value="Edebiyat" className="bg-slate-800">Edebiyat</option>
                                            <option value="Tarih" className="bg-slate-800">Tarih</option>
                                            <option value="Coğrafya" className="bg-slate-800">Coğrafya</option>
                                            <option value="İngilizce" className="bg-slate-800">İngilizce</option>
                                        </select>
                                    </div>
                                )}

                                {(selectedRole === 'teacher' || selectedRole === 'consultant') && (
                                    <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 px-4 py-3 rounded-lg text-sm">
                                        ⚠️ Öğretmen ve danışman hesapları yönetici onayı gerektirir.
                                    </div>
                                )}

                                {error && (
                                    <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-4 py-2 rounded-lg text-sm">
                                        {error}
                                    </div>
                                )}

                                {success && (
                                    <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-200 px-4 py-2 rounded-lg text-sm">
                                        {success}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg disabled:opacity-50"
                                >
                                    {loading ? 'Kayıt Yapılıyor...' : 'Kayıt Ol'}
                                </button>
                            </form>
                        )}



                        {/* Inside Card Footer Links */}
                        {mode === 'login' && isMainRoute && !isAdminRoute && (
                            <div className="mt-6 pt-6 border-t border-white/10 text-center">
                                <p className="text-white/70 text-sm font-medium">
                                    Henüz bir paketiniz yok mu?{' '}
                                    <button onClick={() => {
                                        onClose();
                                        window.location.hash = '#pricing';
                                        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                                    }} className="text-blue-300 font-bold hover:underline transition-all hover:text-blue-200">
                                        Paketleri İncele
                                    </button>
                                </p>
                            </div>
                        )}
                        {mode === 'login' && !isMainRoute && !isAdminRoute && (
                            <div className="mt-6 pt-6 border-t border-white/10 text-center">
                                <p className="text-white/70 text-sm font-medium">
                                    Hesabınız yok mu?{' '}
                                    <button onClick={() => setMode('register')} className="text-blue-300 font-bold hover:underline transition-all hover:text-blue-200">
                                        Hemen Kaydolun
                                    </button>
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ForgotPasswordModal
                isOpen={showForgotPassword}
                onClose={() => setShowForgotPassword(false)}
            />
        </div>
    );
};
